<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import ResponsiveDialog from '@/components/responsive-dialog.vue';

// Confirmation step for assigning a professional the patient has blocked: the backend
// rejected the placement with `blocked_professional`; confirming retries it with the
// explicit override (persisted on the assignment as blocked_override).
interface Props {
  modelValue: boolean;
  caregiverName: string;
  patientName: string;
  busy?: boolean;
}

withDefaults(defineProps<Props>(), { busy: false });
const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'confirm': [];
  'cancel': [];
}>();

const { t } = useI18n();

function onCancel() {
  emit('cancel');
  emit('update:modelValue', false);
}
</script>

<template>
  <responsive-dialog
    :model-value="modelValue"
    max-width="460"
    @update:model-value="$event ? emit('update:modelValue', true) : onCancel()"
  >
    <v-card class="pa-2">
      <v-card-title class="d-flex align-center ga-2">
        <v-icon
          color="warning"
          icon="mdi-alert-outline"
        />
        <span>{{ t('routePlan.blockedOverride.title') }}</span>
      </v-card-title>

      <v-card-text>
        <p class="text-body-2 mb-3">
          {{ t('routePlan.blockedOverride.message',
               { caregiver: caregiverName, patient: patientName }) }}
        </p>
        <p class="text-body-2 text-medium-emphasis mb-0">
          {{ t('routePlan.blockedOverride.hint') }}
        </p>
      </v-card-text>

      <v-card-actions class="px-4 pb-3 pt-0">
        <v-spacer />
        <v-btn
          variant="text"
          :disabled="busy"
          @click="onCancel"
        >
          {{ t('routePlan.blockedOverride.cancel') }}
        </v-btn>
        <v-btn
          color="warning"
          variant="flat"
          :loading="busy"
          @click="emit('confirm')"
        >
          {{ t('routePlan.blockedOverride.confirm') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </responsive-dialog>
</template>
